import { IcAltitude, IcHeight } from '@assets/icons/24x24';
import { Grid, GridItem, HStack, Icon, Text } from '@chakra-ui/react';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectAltitudeUnit, selectHeightSuffix, selectHeightUnit } from '@redux/settings/settings.selectors';
import { convertDistanceUnitFromMeter, formatUnit, HeightUnits } from '@utils/unit.utils';
import { FunctionComponent, SVGProps } from 'react';
import { FormattedMessage } from 'react-intl';

import { ElevationReliabilityEnum, SpatialLocation } from '@/types/sensor/sensor.types';

type Props = {
  maxTransmittedAgl: number | null;
  maxComputedAgl: number | null;
  maxTransmittedAsl: number | null;
  maxComputedAsl: number | null;
  location: SpatialLocation;
};

type ElevationInfoProps = {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  prefix: string;
  value: number | null | undefined;
  unit: HeightUnits;
  suffix: string;
  showSuffix: boolean;
  reliability?: ElevationReliabilityEnum;
  fillRow?: boolean;
};

function ElevationInfoItem({
  icon,
  prefix,
  value,
  unit,
  suffix,
  reliability,
  showSuffix,
  fillRow = false,
}: Readonly<ElevationInfoProps>) {
  return (
    <>
      <GridItem colSpan={1}>
        <HStack gap={1}>
          <Icon as={icon} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
          <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
            <FormattedMessage id={prefix} />
          </Text>
        </HStack>
      </GridItem>
      <GridItem colSpan={fillRow ? 3 : 1}>
        <HStack gap={1} height="100%">
          <Tooltip
            content={
              <Text textStyle="screenStd">
                <FormattedMessage id={`targetInfos.tab.information.elevation.reliability.${reliability}`} />
              </Text>
            }
            disabled={reliability === undefined}
            openDelay={200}
          >
            <Text textStyle="screenStd" fontWeight="medium">
              {formatUnit(unit, convertDistanceUnitFromMeter(unit, value))}
            </Text>
          </Tooltip>
          {showSuffix && (
            <Text textStyle="screenStd" color="neutral.500">
              <FormattedMessage id={suffix} />
            </Text>
          )}
        </HStack>
      </GridItem>
    </>
  );
}

export default function TargetElevationsInformation({
  maxTransmittedAgl,
  maxComputedAgl,
  maxTransmittedAsl,
  maxComputedAsl,
  location,
}: Readonly<Props>) {
  const showHeightSuffix = useAppSelector(selectHeightSuffix);
  const heightUnit = useAppSelector(selectHeightUnit);
  const altitudeUnit = useAppSelector(selectAltitudeUnit);

  return (
    <Grid templateColumns="max-content 1fr max-content 1fr" width="100%" columnGap={1} rowGap="standardXS">
      <ElevationInfoItem
        icon={IcHeight}
        prefix="targetInfos.tab.information.elevation.transmitted"
        unit={heightUnit}
        value={location.elevationAgl?.transmitted}
        suffix="target.agl"
        reliability={location.elevationAgl?.reliability}
        showSuffix={showHeightSuffix}
      />
      <ElevationInfoItem
        icon={IcHeight}
        prefix="targetInfos.tab.information.elevation.max.transmitted"
        unit={heightUnit}
        value={maxTransmittedAgl}
        suffix="target.agl"
        showSuffix={showHeightSuffix}
      />
      <ElevationInfoItem
        icon={IcHeight}
        prefix="targetInfos.tab.information.elevation.computed"
        unit={heightUnit}
        value={location.elevationAgl?.computed}
        suffix="target.agl"
        showSuffix={showHeightSuffix}
      />
      <ElevationInfoItem
        icon={IcHeight}
        prefix="targetInfos.tab.information.elevation.max.computed"
        unit={heightUnit}
        value={maxComputedAgl}
        suffix="target.agl"
        showSuffix={showHeightSuffix}
      />
      <ElevationInfoItem
        icon={IcAltitude}
        prefix="targetInfos.tab.information.elevation.transmitted"
        unit={altitudeUnit}
        value={location.elevationAsl?.transmitted}
        suffix="target.amsl"
        showSuffix={showHeightSuffix}
        reliability={location.elevationAsl?.reliability}
      />
      <ElevationInfoItem
        icon={IcAltitude}
        prefix="targetInfos.tab.information.elevation.max.transmitted"
        unit={altitudeUnit}
        value={maxTransmittedAsl}
        suffix="target.amsl"
        showSuffix={showHeightSuffix}
      />
      <ElevationInfoItem
        icon={IcAltitude}
        prefix="targetInfos.tab.information.elevation.computed"
        unit={altitudeUnit}
        value={location.elevationAsl?.computed}
        suffix="target.amsl"
        showSuffix={showHeightSuffix}
      />
      <ElevationInfoItem
        icon={IcAltitude}
        prefix="targetInfos.tab.information.elevation.max.computed"
        unit={altitudeUnit}
        value={maxComputedAsl}
        suffix="target.amsl"
        showSuffix={showHeightSuffix}
      />
    </Grid>
  );
}
